import { useApolloClient } from '@apollo/client/react';
import { Form, Formik, FormikConfig } from 'formik';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../../../components/Button';
import { FormikEmailInput } from '../../../components/formik/FormEmailInput';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';
import { T, useT } from '../../../translation/src';
import { ProjectAccessLevel, UsersDocument, useProjectAddGuestMutation } from '../../../types/graphqlTypes';
import { AccessLevelSelect, DEFAULT_ACCESS_LEVEL } from '../ProjectAccessPicker';

interface FormValues {
  emails: string[];
}

interface InviteGuestsByEmailProps {
  projectId: string;
  className?: string;
  /** Fires with true while addresses are being entered, false once the field is empty again. */
  onActiveChange?: (active: boolean) => void;
}

/** Reports whether the form holds addresses; lives inside Formik to see the values. */
const ActiveReporter: React.FC<{ active: boolean; onActiveChange?: (active: boolean) => void }> = ({ active, onActiveChange }) => {
  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);
  return null;
};

/**
 * The "add people" field of the share dialog. On its own it is just the field; once an address is
 * in it, the level and the Send button appear underneath. Adding a person is an immediate action,
 * like everything else in that dialog.
 */
const InviteGuestsByEmail_: React.FC2<InviteGuestsByEmailProps> = ({ projectId, className, onActiveChange }) => {
  const addPeople = useT('add people by email', { swc: true });
  const back = useT('back', { swc: true });
  const accessLabel = useT('access', { swc: true });
  const invitationsSent = useT('invitations sent', { swc: true });
  const { somethingWentWrong } = useValidationTranslations();
  const apolloClient = useApolloClient();
  const [addProjectGuest] = useProjectAddGuestMutation();
  const [level, setLevel] = useState<ProjectAccessLevel>(DEFAULT_ACCESS_LEVEL);
  const [sending, setSending] = useState(false);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async ({ emails }, helpers) => {
      if (!emails.length) return;
      setSending(true);
      try {
        await Promise.all(emails.map((email) => addProjectGuest({ variables: { projectId, email, level } })));
        await apolloClient.refetchQueries({ include: [UsersDocument] });
        toast.success(invitationsSent);
        helpers.resetForm();
      } catch {
        toast.error(somethingWentWrong);
      } finally {
        setSending(false);
      }
    },
    [addProjectGuest, apolloClient, projectId, level, invitationsSent, somethingWentWrong],
  );

  const initialValues = useMemo<FormValues>(() => ({ emails: [] }), []);

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ values, resetForm }) => {
        const active = values.emails.length > 0;
        return (
          <Form className={className}>
            <ActiveReporter active={active} onActiveChange={onActiveChange} />
            <FormikEmailInput name="emails" placeholder={addPeople} hideErrorLabel />
            {active && (
              <div className="flex items-center justify-between gap-3 mt-1">
                <Button variant="secondary" size="xs" onClick={() => resetForm()} disabled={sending}>
                  <span className="whitespace-nowrap">{back}</span>
                </Button>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{accessLabel}</span>
                    <AccessLevelSelect value={level} onChange={(next) => setLevel(next ?? DEFAULT_ACCESS_LEVEL)} allowNone={false} />
                  </label>
                  <Button variant="primary" size="xs" type="submit" loading={sending}>
                    <span className="whitespace-nowrap">
                      <T _str="send invitation" swc />
                    </span>
                  </Button>
                </div>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export const InviteGuestsByEmail = memo(InviteGuestsByEmail_);
